import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { onlineshopApiBaseUrl } from '../Filters';
import FilterWrapper from '../../components/FilterWrapper/FilterWrapper';
import { ProductFilterOptionNode } from '../../../../../../Interface/ProductFilterOption';
import { useProductsFilter } from '../../../../../Context/ProductsFilterContext';
import SaleFilterItem from './SaleFilterItem/SaleFilterItem';
import useViewports from '../../../../../Hooks/useViewports';

const filterTarget = [
  'brand_ids',
  'category_ids',
  'sub_category_ids',
  'product_ids',
  'product_size_ids',
  'product_attribute_ids',
  'q',
  'favs_only',
];

const SaleFilter = () => {
  const { xs, sm, md } = useViewports();
  const isMobile = xs || sm || md;
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { filters, registerActiveFilterLabels } = useProductsFilter();

  const onlyRealFiltersExceptItself = new URLSearchParams();

  filterTarget.forEach((filterKey) => {
    const val = searchParams.get(filterKey);
    if (val) onlyRealFiltersExceptItself.set(filterKey, val);
  });

  const isClearance = searchParams.get('is_clearance');
  if (isClearance) {
    onlyRealFiltersExceptItself.set('is_clearance', isClearance);
  }

  const hasStock = searchParams.get('has_stock');
  if (hasStock) {
    onlyRealFiltersExceptItself.set('has_stock', hasStock);
  }

  onlyRealFiltersExceptItself.append(
    'context[]',
    filters.view === 'grid' ? 'product_sizes_count' : 'product_attributes_count'
  );

  const saleFilter = useQuery<ProductFilterOptionNode[]>({
    queryKey: [
      'sale_percent',
      onlyRealFiltersExceptItself.toString(),
      //refetch trigger only when view changes (grid & list) no need for table view refetch because list and table have the same data
      filters.view === 'grid' ? 'grid' : 'list',
    ],
    queryFn: async () => {
      const res = await axios.get(
        `${onlineshopApiBaseUrl}/sales-filter?sortBy=name&${onlyRealFiltersExceptItself.toString()}`
      );
      return res.data;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });

  const { data, isSuccess, isPending, isFetching } = saleFilter;

  const mergedOptions = useMemo<ProductFilterOptionNode[]>(() => {
    const options = new Map<string, ProductFilterOptionNode>();

    (data ?? []).forEach((option) => {
      options.set(option.value, option);
    });

    filters.sale_type.forEach((valueString) => {
      if (options.has(valueString)) return;

      const cachedMatches = queryClient
        .getQueriesData<ProductFilterOptionNode[]>({
          queryKey: ['sale_percent'],
        })
        .flatMap(([, cached]) => cached ?? [])
        .find((cachedItem) => cachedItem.value === valueString);

      if (cachedMatches) {
        options.set(valueString, cachedMatches);
      }
    });

    return Array.from(options.values()).filter((option) => {
      const isActive = filters.sale_type.includes(option.value.toString());
      return (option.count ?? 0) > 0 || isActive;
    });
  }, [data, filters.sale_type, queryClient]);

  // Register active filter labels for sale filters when data is loaded
  useEffect(() => {
    if (mergedOptions.length === 0) return;

    // Register sale_type labels
    if (filters.sale_type.length > 0) {
      const saleTypeLabels = filters.sale_type
        .map((value) => {
          const option = mergedOptions.find(
            (opt) => opt.value.toString() === value.toString()
          );
          if (option) {
            return { value, label: option.name };
          }
          return null;
        })
        .filter(
          (item): item is { value: string; label: string } => item !== null
        );

      if (saleTypeLabels.length > 0) {
        registerActiveFilterLabels('sale_type', saleTypeLabels);
      }
    }

    // Register sale_percent labels (children)
    if (filters.sale_percent.length > 0) {
      const salePercentLabels: { value: string; label: string }[] = [];

      mergedOptions.forEach((saleType) => {
        saleType.children?.forEach((child) => {
          if (filters.sale_percent.includes(child.value.toString())) {
            salePercentLabels.push({
              value: child.value.toString(),
              label: child.name,
            });
          }
        });
      });

      if (salePercentLabels.length > 0) {
        registerActiveFilterLabels('sale_percent', salePercentLabels);
      }
    }
  }, [
    mergedOptions,
    filters.sale_type,
    filters.sale_percent,
    registerActiveFilterLabels,
  ]);

  if (isSuccess && !isPending && !mergedOptions.length) return null;

  return (
    <FilterWrapper
      label="sale %"
      activeFilterKeys={['sale_type', 'sale_percent']}
      {...{
        isFetching,
        isSuccess,
        isPending,
        resultsCount: mergedOptions.length,
        initialOpen: isMobile ? false : true,
        expandable: isMobile ? true : false,
      }}
    >
      {mergedOptions.map((item) => (
        <SaleFilterItem key={item.value} sale={item} />
      ))}
    </FilterWrapper>
  );
};

export default SaleFilter;
